import {loadSceneBundle, parseRoute, payloadMatchesAnchor} from './scene/config'
import {applyTheme} from './ui/theme'
import {showLanding, showStatus} from './ui/landing'

// M2: /x/{tenant}/{anchor} route -> config bundle -> themed landing -> camera
// session -> QR localization (payload-checked) -> frozen world anchor.
async function boot(): Promise<void> {
  const route = parseRoute(location.pathname)
  if (!route) {
    showStatus('this link is missing its scene — scan a sticker to enter')
    return
  }

  let bundle
  try {
    bundle = await loadSceneBundle(route)
  } catch (error) {
    console.error('[config]', error)
    showStatus('scene unavailable — try again in a moment')
    return
  }
  const {tenant, anchor: anchorConfig} = bundle
  applyTheme(tenant)

  showLanding(
    {
      title: tenant.name,
      intro: tenant.copy.landingIntro ?? 'An augmented world is hiding here.',
    },
    async () => {
      const clearBoot = showStatus('waking the world…')
      try {
        // Preflight the camera first: surfaces permission problems with real
        // error names before the engine gets involved.
        const preflight = await navigator.mediaDevices.getUserMedia({
          video: {facingMode: 'environment'},
        })
        for (const track of preflight.getTracks()) track.stop()

        const [{Xr8Session}, {QrLocalizer}, {WorldAnchor}, {plantArJungle}, {HuntState}, {HuntDirector}] =
          await Promise.all([
            import('./ar/xr8-session'),
            import('./ar/qr-localizer'),
            import('./ar/anchor'),
            import('./scene/ar-jungle'),
            import('./hunt/HuntState'),
            import('./hunt/HuntDirector'),
          ])

        const session = new Xr8Session()
        const canvas = document.getElementById('ar-canvas') as HTMLCanvasElement

        const {resolveTapPlacement} = await import('./ar/fallback-placement')

        let anchor: InstanceType<typeof WorldAnchor>
        let warnedWrongSticker = false
        let sightings = 0
        let sightingPlantTimer: ReturnType<typeof setTimeout> | null = null
        const localizer = new QrLocalizer({
          qrSizeM: anchorConfig.qr.sizeM,
          onDetection: ({payload, pose}) => {
            if (!payloadMatchesAnchor(payload, anchorConfig, route)) return
            anchor.addSample(pose, session.camera)
          },
          // Resilient path: the code was SEEN — even if precise pose solving
          // fails on this device, plant at the sticker via a hit test through
          // its on-screen centroid. Vines grow out of the QR either way.
          onSighting: ({payload, centroid}) => {
            if (anchor.state === 'locked') return
            if (!payloadMatchesAnchor(payload, anchorConfig, route)) {
              if (!warnedWrongSticker) {
                warnedWrongSticker = true
                const clear = showStatus('that sticker belongs to a different scene')
                setTimeout(clear, 4000)
              }
              return
            }
            sightings++
            if (sightings === 1) {
              const clear = showStatus('found the mark — hold on it')
              setTimeout(clear, 2500)
            }
            // Give the precise pose 1.2s after the third sighting, then plant
            // through the centroid regardless.
            if (sightings >= 3 && !sightingPlantTimer) {
              const cx = centroid.x
              const cy = centroid.y
              sightingPlantTimer = setTimeout(() => {
                if (anchor.state === 'locked') return
                const hit = resolveTapPlacement(session, cx, cy)
                anchor.lock(hit.position, hit.quaternion, session.camera)
              }, 1200)
            }
          },
        })
        session.extraModules = (xr8) => localizer.modules(xr8)

        await session.start(canvas)
        clearBoot()
        anchor = new WorldAnchor(session.scene)

        const hunt = new HuntState(route.tenantId, bundle.hunt?.id ?? null)
        const clearCoaching = showStatus(tenant.copy.coaching ?? 'point your camera at the sticker')
        let clearFallbackOffer: (() => void) | null = null
        anchor.onLock(() => {
          localizer.stop()
          clearCoaching()
          clearFallbackOffer?.()
          const clear = showStatus('step back slowly — it begins')
          setTimeout(clear, 3500)
          const visitOrdinal = hunt.visit(route.anchorId)
          const memoryUrl = `/api/memory/${route.tenantId}/${route.anchorId}`
          const {jungle, audio} = plantArJungle(session, anchor, anchorConfig, hunt.growthCapForVisit(visitOrdinal), {
            initialBubbles: hunt.getBubbles(route.anchorId),
            onPersistBubbles: (b) => {
              hunt.saveBubbles(route.anchorId, b)
              // Contribute to the communal memory (fire-and-forget).
              void fetch(memoryUrl, {
                method: 'POST',
                headers: {'content-type': 'application/json'},
                body: JSON.stringify({bubbles: b}),
              }).catch(() => {})
            },
          })
          // The wall remembers everyone: merge the communal growth memory in.
          void fetch(memoryUrl)
            .then((r) => (r.ok ? r.json() : null))
            .then((shared: {bubbles?: number[][]; contributors?: number} | null) => {
              if (shared?.bubbles?.length) {
                jungle.loadBubbles(shared.bubbles)
                if ((shared.contributors ?? 0) > 1) {
                  const clear = showStatus(`${shared.contributors} souls have grown this garden`)
                  setTimeout(clear, 3000)
                }
              }
            })
            .catch(() => {})
          new HuntDirector(session, anchor, jungle, bundle, hunt).start()
          // (Microphone reactivity is parked until music enters the picture —
          //  AudioSense stays in the codebase, nothing requests the mic.)
          // Tap routing after lock: held daisy placement > beacons > creature
          // taps (bunny cutie-eyes, mushrooms) > daisy picking.
          void Promise.all([
            import('./interact/Picker'),
            import('./hunt/Beacons'),
            import('three'),
            import('./interact/PinchScale'),
            import('./interact/tapMath'),
          ]).then(([{Picker}, {createBeacons, showBeaconCard}, THREE, {PinchScale}, {screenHit}]) => {
            // Pinch: shrink/grow the whole garden around the sticker.
            const pinch = new PinchScale(canvas, anchor.group)
              const picker = new Picker(
                session,
                anchor.group,
                () => jungle.pickableMeshes,
                () => jungle.makePickedDaisy(),
              )
              const beacons = createBeacons(anchorConfig, jungle.uniforms)
              for (const b of beacons) anchor.group.add(b.group)

              const scratch = new THREE.Vector3()
              canvas.addEventListener('pointerdown', (e) => {
                if (anchor.state !== 'locked') return
                if (pinch.active) return // two-finger gesture, not a tap
                const sx = e.clientX / innerWidth
                const sy = e.clientY / innerHeight
                if (picker.isHolding) {
                  picker.onTap(sx, sy)
                  return
                }
                // Gather every tappable near the tap; the CLOSEST on screen
                // wins (windows scale with each target's projected size).
                let best: {d: number; act: () => void} | null = null
                const consider = (world: typeof scratch, radiusM: number, act: () => void) => {
                  const r = screenHit(session.camera, world, sx, sy, radiusM)
                  if (r.hit && (!best || r.d < best.d)) best = {d: r.d, act}
                }
                for (const b of beacons) {
                  consider(b.group.getWorldPosition(scratch), 0.3, () => showBeaconCard(b.def))
                }
                for (const target of jungle.reactiveTargets()) {
                  consider(
                    jungle.group.localToWorld(scratch.copy(target.localPos)),
                    target.radiusM,
                    () => {
                      target.touch()
                      const clear = showStatus(target.kind === 'rabbit' ? '🐰 boop!' : '🍄 curious…')
                      setTimeout(clear, 1600)
                    },
                  )
                }
                if (best) {
                  ;(best as {act: () => void}).act()
                  return
                }
                picker.onTap(sx, sy)
              })
            },
          ).catch((error) => {
            console.error('[interact]', error)
            showStatus('interactions failed to load — pull to refresh')
          })
          if (new URLSearchParams(location.search).has('install')) {
            void import('./env/InstallMode').then(({startInstallMode}) =>
              startInstallMode(session, anchor),
            )
          }
        })

        if (anchorConfig.allowFallback) {
          // A tap plants immediately, any time before lock.
          canvas.addEventListener('pointerdown', (e) => {
            if (anchor.state === 'locked') return
            const hit = resolveTapPlacement(session, e.clientX / innerWidth, e.clientY / innerHeight)
            anchor.lock(hit.position, hit.quaternion, session.camera)
          })
          // And if nothing has locked after 6s, the world just starts: scan
          // for a center-screen surface and plant there on its own.
          const autoTimer = setTimeout(() => {
            clearCoaching()
            clearFallbackOffer = showStatus('no mark needed — it comes anyway')
            const autoScan = setInterval(() => {
              if (anchor.state === 'locked') {
                clearInterval(autoScan)
                return
              }
              const hit = resolveTapPlacement(session, 0.5, 0.62)
              anchor.lock(hit.position, hit.quaternion, session.camera)
              clearInterval(autoScan)
            }, 700)
            anchor.onLock(() => clearInterval(autoScan))
          }, 6_000)
          anchor.onLock(() => clearTimeout(autoTimer))
        }
      } catch (error) {
        clearBoot()
        const detail = error instanceof Error ? error.message : String(error)
        if (/denied|NotAllowed/i.test(detail)) {
          showStatus('camera permission denied — allow camera for this site and reload')
        } else if (/NotFound|no camera/i.test(detail)) {
          showStatus('no usable camera found on this device')
        } else {
          showStatus(`startup failed: ${detail.slice(0, 120)}`)
        }
        console.error('[boot]', error)
      }
    },
  )
}

boot()
